/**
 * PAPPO — InstructionsPage
 * Aba de instruções para responsáveis: a pipeline completa do sistema,
 * do cadastro ao acompanhamento no dia a dia.
 */

import {
  BookOpen,
  UserPlus,
  BadgeCheck,
  Users,
  Link2,
  ScanEye,
  Bell,
  ShieldAlert,
} from 'lucide-react';
import styles from './InstructionsPage.module.css';

const steps = [
  {
    icon: <UserPlus size={20} />,
    title: '1. Crie sua conta de responsável',
    text: 'Cadastre-se com o seu email e confirme o link que enviamos para ativar a conta. É a sua conta de responsável que comanda tudo — os filhos nunca ficam sozinhos no controle.',
  },
  {
    icon: <BadgeCheck size={20} />,
    title: '2. Verifique sua identidade (Nível 01)',
    text: 'No Painel Parental, envie uma foto do seu RG e uma selfie. Um responsável do PAPPO revisa manualmente. Essa etapa é o que garante que, atrás de cada conta de criança, existe um adulto real e verificado — por isso ela vem antes de adicionar qualquer filho.',
  },
  {
    icon: <Users size={20} />,
    title: '3. Adicione seus filhos',
    text: 'Com a identidade verificada, toque em "Adicionar filho" e crie a conta de cada um (nome de utilizador + senha). Você pode adicionar quantos filhos quiser — cada um entra no PAPPO pelo próprio aparelho com os dados que você definir.',
  },
  {
    icon: <Link2 size={20} />,
    title: '4. Você media todas as amizades',
    text: 'Nenhuma criança fala com ninguém por conta própria. Quando alguém quer conversar com o seu filho, o pedido chega até você — e também ao responsável da outra criança. A conversa só começa depois que os dois lados aprovam. Ligar dois filhos seus também passa pela sua aprovação.',
  },
  {
    icon: <ScanEye size={20} />,
    title: '5. A IA acompanha cada mensagem',
    text: 'Toda mensagem de texto do seu filho (e áudios, que são transcritos) passa por uma análise de inteligência artificial que procura sinais de risco: aliciamento, bullying, drogas, ameaças, conteúdo impróprio e automutilação. Uma rede de proteção adicional garante que os casos mais graves nunca passem despercebidos, mesmo se a IA estiver indisponível.',
  },
  {
    icon: <Bell size={20} />,
    title: '6. Você recebe o alerta e decide',
    text: 'Havendo risco, um alerta aparece no seu Painel Parental na hora. Dali você pode ver a conversa, cortar o contato do seu filho com aquela pessoa, denunciar ou marcar como resolvido. Em sinais de automutilação, mostramos na hora o canal de apoio (CVV — 188).',
  },
  {
    icon: <ShieldAlert size={20} />,
    title: '7. Denúncias viram ação',
    text: 'Quando você denuncia, o caso vai para a equipe de moderação do PAPPO com a mensagem sinalizada e a análise da IA. A equipe pode advertir, silenciar, suspender ou banir o adulto envolvido. O ciclo se fecha: a IA detecta, você decide, a moderação age.',
  },
];

export function InstructionsPage() {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <BookOpen size={28} className={styles.headerIcon} />
          <div>
            <h1 className={styles.title}>Como funciona o PAPPO</h1>
            <p className={styles.subtitle}>
              Do cadastro ao acompanhamento no dia a dia — o caminho, passo a
              passo, de como mantemos o seu filho seguro.
            </p>
          </div>
        </div>

        <div className={styles.steps}>
          {steps.map((s) => (
            <div key={s.title} className={styles.step}>
              <div className={styles.stepIcon}>{s.icon}</div>
              <div>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepText}>{s.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.note}>
          <strong>Você está no controle.</strong> Na aba <strong>Permissões</strong>{' '}
          você decide, por filho, quem pode gerar convites e se cada conversa
          exige a sua aprovação. Também pode adicionar um{' '}
          <strong>segundo responsável</strong> (cônjuge ou guarda compartilhada),
          com os mesmos poderes, para acompanhar junto com você.
        </div>
      </div>
    </div>
  );
}
